import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [credits, setCredits] = useState<number | null>(null);

  const fetchCredits = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .rpc('get_user_total_credits', { p_user_id: user.id });

      if (error) throw error;
      setCredits(data || 0);
    } catch (error) {
      console.error('Error fetching credits:', error);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchCredits();
    } else {
      // Clear credits when user logs out
      setCredits(null);
    }
  }, [user, fetchCredits]);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    try {
      setDropdownOpen(false);
      await signOut();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('[Header] Sign out error:', error);
      navigate('/', { replace: true });
    }
  };

  const navItems = [
    { path: '/explore', label: 'Explore' },
    { path: '/arena', label: 'Arena' },
    { path: '/pricing', label: 'Pricing' },
    { path: '/how-it-works', label: 'How it works' },
  ];

  const isActive = (path: string) => location.pathname === path;

  // Check if on debate view page to allow header to scroll away
  const isDebateViewPage = location.pathname.startsWith('/debate/');

  return (
    <header
      className={`${isDebateViewPage ? '' : 'sticky top-0'} z-50 border-b border-black/[0.08] bg-off-white/85 backdrop-blur-md`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="group flex items-center gap-2.5">
            {/* Two-sides mark: steel-blue vs vermilion */}
            <span className="relative block h-5 w-5 overflow-hidden rounded-[5px] border border-charcoal/15 transition-transform duration-300 ease-out-expo group-hover:rotate-90">
              <span className="absolute inset-0 bg-steel-blue [clip-path:polygon(0_0,100%_0,0_100%)]" />
              <span className="absolute inset-0 bg-vermilion [clip-path:polygon(100%_0,100%_100%,0_100%)]" />
            </span>
            <span className="font-serif text-2xl text-charcoal">Steelman</span>
          </Link>

          {/* Navigation */}
          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  isActive(item.path)
                    ? 'bg-charcoal text-white'
                    : 'text-ink-muted hover:bg-black/[0.05] hover:text-charcoal'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Auth Section */}
          <div className="flex items-center gap-3">
            {user && credits !== null && (
              <Link
                to="/pricing"
                title="Credits remaining"
                className={`hidden items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono text-xs transition-colors sm:flex ${
                  credits < 3
                    ? 'border-vermilion/30 bg-vermilion/10 text-vermilion-deep hover:bg-vermilion/15'
                    : 'border-black/[0.08] bg-white/70 text-charcoal hover:border-charcoal/30'
                }`}
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span className="font-medium">{credits}</span>
                {credits < 3 && <span className="hidden md:inline">· top up</span>}
              </Link>
            )}

            {user ? (
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 transition-colors hover:bg-black/[0.05]"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-charcoal text-sm font-semibold text-white">
                    {profile?.username?.charAt(0).toUpperCase() || 'U'}
                  </span>
                  <span className="hidden text-sm font-medium text-charcoal md:inline">
                    {profile?.username}
                  </span>
                  <svg
                    className={`h-3.5 w-3.5 text-ink-muted transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                <AnimatePresence>
                  {dropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -6, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.98 }}
                      transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                      className="card-frame absolute right-0 mt-2 w-52 overflow-hidden py-1.5"
                      onMouseLeave={() => setDropdownOpen(false)}
                    >
                      <div className="border-b border-hairline px-4 py-2.5">
                        <p className="truncate text-sm font-medium text-charcoal">{profile?.username}</p>
                        {credits !== null && (
                          <p className="mt-0.5 font-mono text-[11px] text-ink-muted">{credits} credits</p>
                        )}
                      </div>
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-charcoal transition-colors hover:bg-black/[0.04]"
                        onClick={() => setDropdownOpen(false)}
                      >
                        Profile
                      </Link>
                      <Link
                        to="/pricing"
                        className="block px-4 py-2 text-sm text-charcoal transition-colors hover:bg-black/[0.04]"
                        onClick={() => setDropdownOpen(false)}
                      >
                        Credits &amp; billing
                      </Link>
                      <button
                        onClick={handleSignOut}
                        className="block w-full px-4 py-2 text-left text-sm text-ink-muted transition-colors hover:bg-black/[0.04] hover:text-charcoal"
                      >
                        Sign out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div className="hidden items-center gap-2 md:flex">
                <Link
                  to="/signin"
                  className="rounded-full px-3.5 py-1.5 text-sm font-medium text-ink-muted transition-colors hover:bg-black/[0.05] hover:text-charcoal"
                >
                  Sign in
                </Link>
                <Link
                  to="/signup"
                  className="rounded-full bg-charcoal px-4 py-2 text-sm font-medium text-white transition-all duration-200 ease-out-expo hover:-translate-y-0.5 hover:shadow-lift"
                >
                  Start a debate
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-charcoal md:hidden"
              aria-label="Toggle menu"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7h16M4 12h16M4 17h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden border-t border-hairline md:hidden"
            >
              <div className="space-y-1 px-1 pb-4 pt-2">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block rounded-xl px-3 py-2.5 text-base font-medium transition-colors ${
                      isActive(item.path)
                        ? 'bg-charcoal text-white'
                        : 'text-charcoal hover:bg-black/[0.05]'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}

                {!user && (
                  <div className="flex gap-2 px-1 pt-2">
                    <Link
                      to="/signin"
                      onClick={() => setMobileMenuOpen(false)}
                      className="btn-secondary flex-1 !py-2.5"
                    >
                      Sign in
                    </Link>
                    <Link
                      to="/signup"
                      onClick={() => setMobileMenuOpen(false)}
                      className="btn-primary flex-1 !py-2.5"
                    >
                      Start a debate
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
